export const STOP_CHART_VIEWPORT_HEIGHT = 500;
export const STOP_CHART_ROW_HEIGHT = 18;
export const STOP_CHART_MIN_PLOT_HEIGHT = 220;
export const STOP_CHART_TICK_FONT_SIZE = 12;
export const STOP_CHART_TICK_FONT_FAMILY = '"Segoe UI", Verdana, Helvetica, sans-serif';
export const STOP_CHART_LABEL_FONT = `${STOP_CHART_TICK_FONT_SIZE}px ${STOP_CHART_TICK_FONT_FAMILY}`;
export const STOP_CHART_LABEL_MARGIN = 16;
export const STOP_CHART_LABEL_WIDTH_MULTIPLIER = 1.15;
export const STOP_CHART_LABEL_PADDING_BUFFER = 28;
export const STOP_CHART_FALLBACK_CHAR_WIDTH = 7;

export interface StopChartTickFont {
    size: number;
    family: string;
}

export interface StopChartLayout {
    height: number;
    bottomPadding: number;
    naturalHeight: number;
    tickFont: StopChartTickFont;
}

export interface StopChartLayoutOptions {
    rowHeight?: number;
    minPlotHeight?: number;
    labelFont?: string;
    labelMargin?: number;
    labelWidthMultiplier?: number;
    labelPaddingBuffer?: number;
    fallbackCharWidth?: number;
    measureLabelWidth?: (text: string, font: string) => number;
}

export function getStopChartTickFont(): StopChartTickFont {
    return {
        size: STOP_CHART_TICK_FONT_SIZE,
        family: STOP_CHART_TICK_FONT_FAMILY,
    };
}

export const getNextActiveBarIndex = (activeBarIndex: number | null, clickedBarIndex: number): number | null => {
    return activeBarIndex === clickedBarIndex ? null : clickedBarIndex;
};

export function measureChartLabelWidth(
    text: string,
    font: string = STOP_CHART_LABEL_FONT,
    fallbackCharWidth: number = STOP_CHART_FALLBACK_CHAR_WIDTH,
): number {
    const normalizedText = String(text ?? '');
    if (!normalizedText) {
        return 0;
    }

    if (typeof document !== 'undefined') {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        if (context) {
            context.font = font;
            return context.measureText(normalizedText).width;
        }
    }

    return normalizedText.length * fallbackCharWidth;
}

export function estimateRotatedLabelPadding(
    labels: string[] | null | undefined,
    options?: StopChartLayoutOptions,
): number {
    const labelMargin = options?.labelMargin ?? STOP_CHART_LABEL_MARGIN;
    const labelWidthMultiplier = options?.labelWidthMultiplier ?? STOP_CHART_LABEL_WIDTH_MULTIPLIER;
    const labelFont = options?.labelFont ?? STOP_CHART_LABEL_FONT;
    const fallbackCharWidth = options?.fallbackCharWidth ?? STOP_CHART_FALLBACK_CHAR_WIDTH;
    const measureLabelWidth = options?.measureLabelWidth ?? ((text: string, font: string) => {
        return measureChartLabelWidth(text, font, fallbackCharWidth);
    });

    const normalizedLabels = (labels || [])
        .map(label => String(label ?? '').trim())
        .filter(label => label.length > 0);
    if (!normalizedLabels.length) {
        return labelMargin;
    }

    const maxLabelWidth = normalizedLabels.reduce((maxWidth, label) => {
        return Math.max(maxWidth, measureLabelWidth(label, labelFont));
    }, 0);

    return Math.ceil(maxLabelWidth * labelWidthMultiplier) + labelMargin;
}

export function buildStopChartLayout(
    labels: string[] | null | undefined,
    options?: StopChartLayoutOptions,
): StopChartLayout {
    const tickFont = getStopChartTickFont();
    const normalizedLabels = (labels || [])
        .map(label => String(label ?? '').trim())
        .filter(label => label.length > 0);
    if (!normalizedLabels.length) {
        return {
            height: 0,
            bottomPadding: 0,
            naturalHeight: 0,
            tickFont,
        };
    }

    const rowHeight = options?.rowHeight ?? STOP_CHART_ROW_HEIGHT;
    const minPlotHeight = options?.minPlotHeight ?? STOP_CHART_MIN_PLOT_HEIGHT;
    const labelPaddingBuffer = options?.labelPaddingBuffer ?? STOP_CHART_LABEL_PADDING_BUFFER;
    const plotHeight = computePlotHeight(normalizedLabels.length, rowHeight, minPlotHeight);
    const bottomPadding = estimateRotatedLabelPadding(normalizedLabels, options) + labelPaddingBuffer;
    const naturalHeight = plotHeight + bottomPadding;

    return {
        height: STOP_CHART_VIEWPORT_HEIGHT,
        bottomPadding,
        naturalHeight,
        tickFont,
    };
}

function computePlotHeight(
    stopCount: number,
    rowHeight: number,
    minPlotHeight: number,
): number {
    if (stopCount <= 0) {
        return 0;
    }

    return Math.max(stopCount * rowHeight, minPlotHeight);
}

export function computeStopChartHeight(
    labels: string[] | null | undefined,
    options?: StopChartLayoutOptions,
): number {
    return buildStopChartLayout(labels, options).height;
}

export type RidershipLayerVisibilityKey = 'board' | 'alight' | 'load';

export interface RidershipLayerVisibility {
    board: boolean;
    alight: boolean;
    load: boolean;
}

export const getLayerVisibilityByDatasetIndex = (
    datasetIndex: number | null | undefined,
): RidershipLayerVisibilityKey | null => {
    if (datasetIndex === 0) {
        return 'board';
    }
    if (datasetIndex === 1) {
        return 'alight';
    }
    if (datasetIndex === 2) {
        return 'load';
    }
    return null;
};

export const buildBarColorSeries = (
    barsCount: number,
    activeBarIndex: number | null,
    activeColor: string,
    inactiveColor: string,
): string[] => {
    return Array.from({ length: barsCount }, (_value, index) => {
        return activeBarIndex === null || activeBarIndex === index
            ? activeColor
            : inactiveColor;
    });
};

export const findStopIndexByStopId = (
    stopIds: Array<string | null | undefined> | undefined,
    stopId: string | number | null | undefined,
): number => {
    const normalizedStopId = stopId === null || stopId === undefined
        ? ''
        : String(stopId).trim();
    if (!normalizedStopId) {
        return -1;
    }

    return (stopIds || []).findIndex(id => String(id ?? '').trim() === normalizedStopId);
};

export interface StopPopupLike {
    stopId?: string;
    stopName?: string;
    boarding?: number;
    alighting?: number;
    load?: number;
    lat?: number;
    lon?: number;
}

interface StopPinLike {
    location?: [number, number];
    options?: {
        stopId?: string;
        stopName?: string;
        boarding?: number;
        alighting?: number;
        load?: number;
        [key: string]: unknown;
    };
}

interface BuildStopPopupFromBarSelectionInput {
    stopId?: string;
    stopLabel?: string;
    boarding?: number;
    alighting?: number;
    load?: number;
    loadPin?: StopPinLike | null;
    boardPin?: StopPinLike | null;
    alightPin?: StopPinLike | null;
}

const parseStopNameFromLabel = (stopLabel: string | undefined): string | undefined => {
    if (!stopLabel) {
        return undefined;
    }
    const labelTrimmed = stopLabel.trim();
    if (!labelTrimmed) {
        return undefined;
    }
    const idSuffixMarker = labelTrimmed.lastIndexOf(' (');
    if (idSuffixMarker <= 0) {
        return labelTrimmed;
    }
    return labelTrimmed.slice(0, idSuffixMarker).trim();
};

const toNumberOrUndefined = (value: unknown): number | undefined => {
    if (typeof value === 'number' && Number.isFinite(value)) {
        return value;
    }
    return undefined;
};

export const buildStopPopupFromBarSelection = (
    input: BuildStopPopupFromBarSelectionInput,
): StopPopupLike => {
    const pin = input.loadPin || input.boardPin || input.alightPin;
    const pinOptions = pin?.options || {};
    const lat = toNumberOrUndefined(pin?.location?.[0]);
    const lon = toNumberOrUndefined(pin?.location?.[1]);
    const rawStopId = pinOptions.stopId ?? input.stopId;
    const stopId = rawStopId === null || rawStopId === undefined
        ? undefined
        : String(rawStopId).trim() || undefined;

    return {
        stopId,
        stopName: (typeof pinOptions.stopName === 'string' && pinOptions.stopName.trim())
            ? pinOptions.stopName
            : parseStopNameFromLabel(input.stopLabel),
        boarding: toNumberOrUndefined(input.boarding) ?? toNumberOrUndefined(pinOptions.boarding),
        alighting: toNumberOrUndefined(input.alighting) ?? toNumberOrUndefined(pinOptions.alighting),
        load: toNumberOrUndefined(input.load) ?? toNumberOrUndefined(pinOptions.load),
        lat,
        lon,
    };
};
